import { inject, injectable } from 'inversify';
import { IEntitiesAction } from '@Redux/IEntitiesAction';
import { Club, ClubCategory, ClubStatus, ClubWebsite, Nullable } from '@Models';
import { TYPES } from '@Types';
import { RequestAction } from '@Repository/RequestAction';
import { MyClubRepository, RequestClubsSuccess } from '@Repository/federation';
import myClubsSlice from '@Redux/reducers/federation/my-clubs.slice';
import { PayloadAction } from '@reduxjs/toolkit';
import { Result } from '@Redux/type';
import { v4 as uuidv4 } from 'uuid';
import { RequestClubSuccess } from '@Repository/federation/response/request-club.success';
import { ClubParams } from '@Components/federation/club/new/club.new.validate';

@injectable()
export class MyClubsAction extends IEntitiesAction<Club, RequestClubsSuccess> {
  @inject(TYPES.Repository.MyClubRepository)
  private _repository!: MyClubRepository;

  protected fetchDataSuccess(item: Club[]): PayloadAction<Club[]> {
    return myClubsSlice.actions.setClubs(item);
  }

  protected cacheDataSuccess(item: Club[]): PayloadAction<Club[]> {
    return myClubsSlice.actions.setClubs(item);
  }

  protected haveError(message: string): PayloadAction<string> {
    return myClubsSlice.actions.setError(message);
  }

  protected areLoading(bool: boolean): PayloadAction<boolean> {
    return myClubsSlice.actions.setLoading(bool);
  }

  protected async getItems(): Promise<RequestAction> {
    return await this._repository.getAll({ locale: null });
  }

  protected async getCachedItems(): Promise<Nullable<Club[]>> {
    return null;
  }

  public create =
    (values: ClubParams): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const club: Club = {
        id: uuidv4(),
        name: values.name,
        notes: values.notes,
        country: values.country,
        location: values.location,
        contact: values.contact,
        members: values.members,
        meets: values.meets,
        status: ClubStatus.active,
        websites: (values.websites ?? []).map(
          (website) =>
            ({
              id: '',
              link: website.link,
              isDeleted: false,
            }) satisfies ClubWebsite as ClubWebsite,
        ),
        categories: (values.categories ?? []).map(
          (category) =>
            ({
              id: '',
              name: category.name,
              categoryId: category.categoryId,
              isDeleted: false,
            }) satisfies ClubCategory as ClubCategory,
        ),
        latitude: null,
        longitude: null,
      } satisfies Club as Club;

      const response = await this._repository.create(club, { locale: null });

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestClubSuccess;
      dispatch(myClubsSlice.actions.addClub(success.payload));
      dispatch(this.areLoading(false));
    };

  public update =
    (clubId: string, values: ClubParams): Result<void> =>
    async (dispatch, getState) => {
      dispatch(this.areLoading(true));

      const club = getState().myClubs.entity?.find(
        (club) => club.id === clubId,
      );
      if (!club) {
        dispatch(this.haveError('Club not found'));
        dispatch(this.areLoading(false));
        return;
      }

      const updateClub: Club = {
        ...club,
        name: values.name,
        notes: values.notes,
        country: values.country,
        location: values.location,
        contact: values.contact,
        members: values.members,
        meets: values.meets,
        websites: (values.websites ?? []).map(
          (website) =>
            ({
              id: website.id,
              link: website.link,
              isDeleted: website.isDeleted,
            }) satisfies ClubWebsite as ClubWebsite,
        ),
        categories: (values.categories ?? []).map(
          (category) =>
            ({
              id: category.id,
              name: category.name,
              categoryId: category.categoryId,
              isDeleted: category.isDeleted,
            }) satisfies ClubCategory as ClubCategory,
        ),
      } satisfies Club as Club;

      const response = await this._repository.update(clubId, updateClub, {
        locale: null,
      });

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestClubSuccess;
      dispatch(myClubsSlice.actions.updateClub(success.payload));
      dispatch(this.areLoading(false));
    };
}
